import { Flex, Spacing } from '@billy/design-system'
import isEqual from 'lodash/isEqual'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'

import { DocumentHtmlPreview } from '@features/invoices/elements/DocumentHtmlPreview'
import { useInvoicePreview } from '@features/invoices/hooks/useInvoicePreview'
import { InvoicePayload } from '@features/invoices/types/invoicePayload'
import { ZervantTemplateSettings } from '@features/invoicingSettings/types/templates/zervantTemplateSettings'

import { useInvoiceDraftPayload } from '../../../../hooks/useInvoiceDraftPayload'
import { useInvoiceEditorDocumentType } from '../../../../hooks/useInvoiceEditorDocumentType'
import { InvoicePreviewAppearanceSettings } from './elements/InvoicePreviewAppearanceSettings'
import * as Styled from './styles'
import { AppearanceLogo } from './types/appearanceLogo'

export const InvoiceEditorPreview = (): ReactElement => {
  const [invoiceDraftPayload, setInvoiceDraftPayload] = useState<InvoicePayload>()
  const appearanceOverridesRef = useRef<{
    customTemplateSettings?: ZervantTemplateSettings
    logoOverride?: AppearanceLogo
  }>({})
  const { documentType } = useInvoiceEditorDocumentType()
  const { getInvoiceDraftPayload, isLoading: isLoadingPreviewPayload } = useInvoiceDraftPayload()
  const { htmlFiles, isLoading: isLoadingInvoicePreview } = useInvoicePreview({ invoiceDraftPayload, documentType })

  const isLoading = isLoadingPreviewPayload || isLoadingInvoicePreview

  const getInvoiceDraftPayloadWithAppearance = useCallback(() => {
    const { customTemplateSettings, logoOverride } = appearanceOverridesRef.current
    const invoiceDraftPayload = getInvoiceDraftPayload(documentType, undefined, customTemplateSettings)

    if (logoOverride === undefined || !invoiceDraftPayload.invoicer) {
      return invoiceDraftPayload
    }

    return {
      ...invoiceDraftPayload,
      invoicer: {
        ...invoiceDraftPayload.invoicer,
        tradeName: {
          ...invoiceDraftPayload.invoicer.tradeName,
          logo: logoOverride,
        },
      },
    }
  }, [documentType, getInvoiceDraftPayload])

  const updateInvoicePreviewAppearance = useCallback(
    (customTemplateSettings?: ZervantTemplateSettings, logoOverride?: AppearanceLogo) => {
      appearanceOverridesRef.current = { customTemplateSettings, logoOverride }
      setInvoiceDraftPayload(getInvoiceDraftPayloadWithAppearance())
    },
    [getInvoiceDraftPayloadWithAppearance],
  )

  useEffect(() => {
    if (!isLoadingPreviewPayload) {
      // The appearance live preview can apply its overrides before the payload data is ready, so the
      // payload is rebuilt from those overrides instead of from the plain defaults. Skipping the
      // update when nothing changed avoids a duplicate preview request.
      const payload = getInvoiceDraftPayloadWithAppearance()

      setInvoiceDraftPayload((currentPayload) => (isEqual(currentPayload, payload) ? currentPayload : payload))
    }
    // no need to depend on getInvoiceDraftPayloadWithAppearance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPreviewPayload])

  return (
    <Styled.InvoiceEditorPreviewWrapper>
      <Flex gap={Spacing.S20} justifyContent="center">
        <Flex alignItems="center" flexDirection="column">
          <DocumentHtmlPreview htmlFiles={htmlFiles} isLoading={isLoading} />
        </Flex>
        <InvoicePreviewAppearanceSettings
          documentType={documentType}
          isPreviewLoading={isLoading}
          updateInvoicePreviewAppearance={updateInvoicePreviewAppearance}
        />
      </Flex>
    </Styled.InvoiceEditorPreviewWrapper>
  )
}
